import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import Dashboard from './components/Dashboard';
import Footer from './components/Footer';
import Toast from './components/Toast';
import QRPage from './components/QRPage';
import Login from './components/Login';
import AdminManagement from './components/AdminManagement';
import SuccessPage from './components/SuccessPage';
import MfaSetup from './components/MfaSetup';
import AuditLogs from './components/AuditLogs';
import RegisterLayout from './pages/RegisterLayout';
import PersonalInfoPage from './pages/PersonalInfoPage';
import MedicalInfoPage from './pages/MedicalInfoPage';
import { ROUTES } from './routes';

// Configure the live production API URL
const API_BASE_URL = 'https://hospital-clinic.harrydenny2712.workers.dev';

function GuestPatientRegister({ onStart }) {
  useEffect(() => {
    onStart();
  }, [onStart]);

  return (
    <div className="page-content" style={{ textAlign: 'center', padding: '80px 24px', color: 'var(--text-secondary)' }}>
      Loading patient portal...
    </div>
  );
}

export default function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const editingPatient = location.state?.patient ?? null;

  const [patients, setPatients] = useState([]);
  const [toasts, setToasts] = useState([]);

  const [currentUser, setCurrentUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('hc_current_user') || 'null'); }
    catch { return null; }
  });

  const [admins, setAdmins] = useState([]);

  const getAuthHeaders = useCallback(() => {
    const headers = { 'Content-Type': 'application/json' };
    if (currentUser?.token) {
      headers['Authorization'] = `Bearer ${currentUser.token}`;
    }
    return headers;
  }, [currentUser]);

  useEffect(() => {
    if (currentUser && (currentUser.role === 'admin' || currentUser.role === 'superadmin')) {
      fetch(`${API_BASE_URL}/api/patients`, { headers: getAuthHeaders() })
        .then(res => res.json())
        .then(data => setPatients(data))
        .catch(err => {
          console.error('Failed to fetch patients:', err);
          addToast('Error loading patient reports.', 'error');
        });
    } else {
      setPatients([]);
    }

    if (currentUser && currentUser.role === 'superadmin') {
      fetch(`${API_BASE_URL}/api/admins`, { headers: getAuthHeaders() })
        .then(res => res.json())
        .then(data => setAdmins(data))
        .catch(err => {
          console.error('Failed to fetch admins:', err);
          addToast('Error loading admins list.', 'error');
        });
    } else {
      setAdmins([]);
    }
  }, [currentUser, getAuthHeaders]);

  useEffect(() => {
    // Only persist admin sessions. Patients shouldn't be remembered if they close the site.
    if (currentUser && currentUser.role !== 'patient') {
      localStorage.setItem('hc_current_user', JSON.stringify(currentUser));
    } else {
      localStorage.removeItem('hc_current_user');
    }

    if (!currentUser || currentUser.role === 'patient') return;

    let timeout;
    const resetTimer = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        handleLogout();
        // The addToast won't work perfectly if handleLogout forces reload, 
        // but handleLogout here redirects to / which reloads anyway, so user will just see login screen.
      }, 60 * 60 * 1000); // 1 hour
    };

    window.addEventListener('mousemove', resetTimer);
    window.addEventListener('keydown', resetTimer);
    resetTimer();

    return () => {
      clearTimeout(timeout);
      window.removeEventListener('mousemove', resetTimer);
      window.removeEventListener('keydown', resetTimer);
    };
  }, [currentUser]);

  const addToast = (message, type = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3500);
  };

  const handleLogin = async (username, password, mfaCode = null) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password, mfaCode })
      });
      const data = await res.json();
      if (!res.ok) {
        addToast(data.error || 'Login failed.', 'error');
        return false;
      }
      if (data.requireMfa) {
        return { requireMfa: true };
      }
      if (data.success) {
        setCurrentUser({ ...data.user, token: data.token });
        navigate(ROUTES.DASHBOARD);
        addToast(`Welcome back, ${data.user.name}! 🔐`);
        return true;
      }
      return false;
    } catch (err) {
      console.error('Login error:', err);
      addToast('Cannot connect to login server.', 'error');
      return false;
    }
  };

  const startPatientSession = useCallback(() => {
    setCurrentUser({ role: 'patient', name: 'Patient' });
  }, []);

  const handleMfaComplete = () => {
    setCurrentUser(prev => ({ ...prev, mfaEnabled: 1 }));
    addToast('MFA Setup complete! Your account is now secure.');
  };

  const handlePatientAccess = () => {
    startPatientSession();
    navigate(ROUTES.REGISTER_PERSONAL);
    addToast('Patient session started. Secure Form loaded! 📋');
  };

  const handleLogout = () => {
    setCurrentUser(null);
    localStorage.removeItem('hc_current_user');
    window.location.href = '/';
  };

  const handleRegisterCancel = () => {
    if (editingPatient) {
      navigate(ROUTES.DASHBOARD);
    } else if (currentUser?.role === 'patient') {
      handleLogout();
    } else {
      navigate(ROUTES.HOME);
    }
  };

  const handleAddAdmin = async (newAdmin) => {
    if (currentUser?.role !== 'superadmin') {
      addToast('Access denied.', 'error');
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/api/admins`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(newAdmin)
      });
      const data = await res.json();
      if (!res.ok) {
        addToast(data.error || 'Failed to add admin.', 'error');
        return;
      }
      if (data.success) {
        setAdmins((prev) => [...prev, data.admin]);
        addToast(`New account for ${newAdmin.name} created! 🛡️`);
      }
    } catch (err) {
      console.error('Add admin error:', err);
      addToast('Network error while adding admin.', 'error');
    }
  };

  const savePatient = async (data) => {
    try {
      const isEdit = !!editingPatient;
      const res = await fetch(`${API_BASE_URL}/api/patients`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      });
      const resData = await res.json();
      if (!res.ok) {
        addToast(resData.error || 'Failed to save report.', 'error');
        return;
      }
      if (resData.success) {
        const savedPatient = resData.patient;
        if (isEdit) {
          setPatients(prev => prev.map(p => p.id === savedPatient.id ? savedPatient : p));
          addToast('Record updated successfully! ✅');
          navigate(ROUTES.DASHBOARD);
        } else {
          setPatients(prev => [savedPatient, ...prev]);
          addToast('ADR report submitted successfully! ✅');
          if (currentUser?.role === 'patient') {
            navigate(ROUTES.SUCCESS);
          } else {
            navigate(ROUTES.DASHBOARD);
          }
        }
      }
    } catch (err) {
      console.error('Save patient error:', err);
      addToast('Network error while saving report.', 'error');
    }
  };

  const deletePatient = async (id) => {
    if (currentUser?.role !== 'superadmin') {
      addToast('Access Denied: Only Super Admins can delete records.', 'error');
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/api/patients/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (!res.ok) {
        addToast(data.error || 'Failed to delete record.', 'error');
        return;
      }
      if (data.success) {
        setPatients(prev => prev.filter(p => p.id !== id));
        addToast('Record deleted.', 'info');
      }
    } catch (err) {
      console.error('Delete patient error:', err);
      addToast('Network error while deleting record.', 'error');
    }
  };

  const handleEditPatient = (patient) => {
    navigate(ROUTES.REGISTER_PERSONAL, { state: { patient } });
  };

  const accessiblePatients = (currentUser?.role === 'admin' || currentUser?.role === 'superadmin')
    ? patients
    : [];

  const registerRoutes = (
    <Route
      path={ROUTES.REGISTER}
      element={
        <RegisterLayout
          patient={editingPatient}
          onSubmit={savePatient}
          onLogout={handleRegisterCancel}
        />
      }
    >
      <Route index element={<Navigate to="personal" replace />} />
      <Route path="personal" element={<PersonalInfoPage onCancel={handleRegisterCancel} />} />
      <Route path="medical" element={<MedicalInfoPage onCancel={handleRegisterCancel} />} />
    </Route>
  );

  const renderGuestRoutes = () => (
    <>
      <Route path={ROUTES.HOME} element={<Login onLogin={handleLogin} onPatientAccess={handlePatientAccess} />} />
      <Route path={`${ROUTES.REGISTER}/*`} element={<GuestPatientRegister onStart={startPatientSession} />} />
      <Route path="*" element={<Navigate to={ROUTES.HOME} replace />} />
    </>
  );

  const renderPatientRoutes = () => (
    <>
      {registerRoutes}
      <Route path={ROUTES.SUCCESS} element={<SuccessPage onLogout={handleLogout} />} />
      <Route path="*" element={<Navigate to={ROUTES.REGISTER_PERSONAL} replace />} />
    </>
  );

  const renderStaffRoutes = () => (
    <>
      <Route path={ROUTES.HOME} element={<Hero />} />
      {registerRoutes}
      <Route path={ROUTES.DASHBOARD} element={
        <div className="page-content">
          <Dashboard
            patients={accessiblePatients}
            onDelete={deletePatient}
            onEdit={handleEditPatient}
            currentUser={currentUser}
          />
        </div>
      } />
      <Route path={ROUTES.QR} element={
        <div className="page-content">
          <QRPage />
        </div>
      } />
      <Route
        path={ROUTES.ADMIN}
        element={
          currentUser.role === 'superadmin'
            ? <div className="page-content"><AdminManagement admins={admins} onAddAdmin={handleAddAdmin} /></div>
            : <Navigate to={ROUTES.DASHBOARD} replace />
        }
      />
      <Route
        path={ROUTES.AUDIT_LOGS}
        element={
          currentUser.role === 'superadmin'
            ? <div className="page-content"><AuditLogs token={currentUser.token} /></div>
            : <Navigate to={ROUTES.DASHBOARD} replace />
        }
      />
      <Route path={ROUTES.SUCCESS} element={<Navigate to={ROUTES.DASHBOARD} replace />} />
      <Route path="*" element={<Navigate to={ROUTES.HOME} replace />} />
    </>
  );

  return (
    <div className="app">
      <Navbar
        patientCount={accessiblePatients.length}
        currentUser={currentUser}
        onLogout={handleLogout}
      />
      <main>
        <Routes>
          {!currentUser && renderGuestRoutes()}
          {currentUser?.role === 'patient' && renderPatientRoutes()}
          {(currentUser?.role === 'admin' || currentUser?.role === 'superadmin') && (
            currentUser.role === 'superadmin' && !currentUser.mfaEnabled 
              ? <Route path="*" element={<MfaSetup token={currentUser.token} onComplete={handleMfaComplete} />} />
              : renderStaffRoutes()
          )}
        </Routes>
      </main>
      <Footer />
      <div className="toast-container">
        {toasts.map(t => <Toast key={t.id} message={t.message} type={t.type} />)}
      </div>
    </div>
  );
}
